<template>
	<div class="pa-2">
		<!-- calculate salaries -->
		<v-dialog v-model="dialog" max-width="300">
			<v-form ref="form" @submit.prevent="calculateEmployeesSalaries">
				<v-card>
					<v-card-title>
						<span class="me-n6">
							{{$T('calculate-salaries-for-selected-month')}}
						</span>
						<v-spacer />
						<v-btn icon :disabled="submitLoading" @click="dialog = false">
							<v-icon>$close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<v-row dense>
							<v-col cols="12">
								<bee-date-picker
									v-model="dialogData.month"
									:input-label="$T('inputs.date')"
									menu-picker
									type="month"
									:input-props="{
										readonly: true,
										dense: true,
										outlined: true,
										prependInnerIcon: '$calendar'
									}"
									hide-details
									:rules="[rules.required]"
								/>
							</v-col>
							<v-col cols="12">
								<v-autocomplete
									v-model="dialogData.sectionId"
									:label="$T('inputs.section-name')"
									:items="sections"
									item-text="name"
									item-value="id"
									dense
									outlined
									hide-details
									:rules="[() => Boolean(dialogData.sectionId || dialogData.employeeIds.length)]"
								/>
							</v-col>
							<v-col cols="12">
								<v-autocomplete
									v-model="dialogData.employeeIds"
									:label="$T('inputs.employees')"
									:items="employees"
									item-text="arabicFullName"
									item-value="employeeId"
									multiple
									dense
									outlined
									hide-details
									:rules="[() => Boolean(dialogData.sectionId || dialogData.employeeIds.length)]"
								/>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer/>
						<v-btn type="submit" color="success" :loading="submitLoading">{{$T('save')}}</v-btn>
						<v-btn :disabled="submitLoading" @click="dialog = false">{{$T('close')}}</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- details dialog -->
		<v-dialog v-model="detailsDialog" max-width="500">
			<v-card>
				<v-card-title>
					<span class="me-n6">
						{{$T('salary-details')}}
					</span>
					<v-spacer />
					<v-btn icon :disabled="submitLoading" @click="detailsDialog = false">
						<v-icon>$close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pb-0">
					<v-row dense>
						<!-- base salary -->
						<v-col md="4" sm="6" cols="12" class="py-2">
							<v-text-field
								v-model="detailsData.baseSalary"
								:label="$T('inputs.base-salary')"
								:loading="detailsLoading"
								readonly
								dense
								outlined
								hide-details
							/>
						</v-col>

						<!-- absence-discount -->
						<v-col md="4" sm="6" cols="12" class="py-2">
							<v-text-field
								v-model="detailsData.absenceDiscount"
								:label="$T('inputs.absence-discount')"
								:loading="detailsLoading"
								readonly
								dense
								outlined
								hide-details
							/>
						</v-col>
						
						<!-- delay-discount -->
						<v-col md="4" sm="6" cols="12" class="py-2">
							<v-text-field
								v-model="detailsData.delayDiscount"
								:label="$T('inputs.delay-discount')"
								:loading="detailsLoading"
								readonly
								dense
								outlined
								hide-details
							/>
						</v-col>
						
						<!-- penalties -->
						<v-col md="4" sm="6" cols="12" class="py-2">
							<v-text-field
								v-model="detailsData.financialPenalties"
								:label="$T('inputs.penalties')"
								:loading="detailsLoading"
								readonly
								dense
								outlined
								hide-details
							/>
						</v-col>
						
						<!-- rewards -->
						<v-col md="4" sm="6" cols="12" class="py-2">
							<v-text-field
								v-model="detailsData.financialRewards"
								:label="$T('inputs.rewards')"
								:loading="detailsLoading"
								readonly
								dense
								outlined
								hide-details
							/>
						</v-col>
						
						<!-- additional -->
						<v-col md="4" sm="6" cols="12" class="py-2">
							<v-text-field
								v-model="detailsData.additional"
								:label="$T('inputs.additional')"
								:loading="detailsLoading"
								readonly
								dense
								outlined
								hide-details
							/>
						</v-col>
					</v-row>

					<v-row dense class="mb-1">
						<!-- recompense -->
						<v-col sm="6" cols="12" class="py-2">
							<div class="text-body-1 mb-1">{{$T('recompenses')}}</div>
							<bee-handy-table
								:headers="recompensesHeaders"
								:items="detailsData.recompenses"
								:loading="detailsLoading"
								:items-per-page="-1"
								dense
								hide-default-footer
								:data-table-props="{ disableSort: true }"
							></bee-handy-table>
						</v-col>
						
						<!-- deduction -->
						<v-col sm="6" cols="12" class="py-2">
							<div class="text-body-1 mb-1">{{$T('deductions')}}</div>
							<bee-handy-table
								:headers="deductionsHeaders"
								:items="detailsData.deductions"
								:loading="detailsLoading"
								:items-per-page="-1"
								dense
								hide-default-footer
								:data-table-props="{ disableSort: true }"
							></bee-handy-table>
						</v-col>
					</v-row>

					<!-- final salary -->
					<div cols="12" class="d-flex justify-center align-center text-center">
						<span class="text-body-1 me-2">{{$T('final-salary')}}:</span>
						<span class="primary--text text-h6">
							{{detailsData.finalSalary.toLocaleString()}}
						</span>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer/>
					<v-btn color="success" :loading="submitLoading" @click="detailsDialog = false">
						{{$T('close')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- filter -->
		<v-row align="center" dense class="mt-0 mb-1">
			<!-- date -->
			<!-- <v-col lg="2" md="3" cols="6">
				<v-menu
					ref="menu"
					:close-on-content-click="false"
					:return-value.sync="filterData.searchDate"
					:disabled="loading"
					transition="scale-transition"
					offset-y
					max-width="290px"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							v-model="filterData.searchDate"
							:label="$T('inputs.date')"
							prepend-icon="mdi-chevron-right"
							append-outer-icon="mdi-chevron-left"
							outlined
							dense
							readonly
							:disabled="loading"
							hide-details
							@click:prepend="filterData.searchDate = moment(filterData.searchDate).subtract(1, 'month').format('YYYY-MM');"
							@click:append-outer="filterData.searchDate = moment(filterData.searchDate).add(1, 'month').format('YYYY-MM');"
							v-bind="attrs"
							v-on="on"
						></v-text-field>
					</template>
					<v-date-picker
						v-model="filterData.searchDate"
						type="month"
						:max="new Date().toISOString().slice(0, 10)"
						no-title
						scrollable
						@change="$refs.menu.save(filterData.searchDate)"
					/>
				</v-menu>
			</v-col> -->

			<v-col cols="2">
				<bee-date-picker
					v-model="filterData.fromDate"
					input-label="من"
					:input-props="{ outlined: true, dense: true, prependInnerIcon: '$calendar' }"
					:months-over-max="5"
					menu-picker
					calendar
					no-title
					hide-details></bee-date-picker>
			</v-col>
			<v-col cols="2">
				<bee-date-picker
				v-model="filterData.toDate"
				input-label="إلى"
				:input-props="{ outlined: true, dense: true, prependInnerIcon: '$calendar' }"
				:months-over-max="5"
				menu-picker
				calendar
				no-title
				hide-details></bee-date-picker>
			</v-col>
			<!-- sectionId -->
			<v-col lg="2" md="3" cols="6">
				<v-autocomplete
					v-model="filterData.sectionId"
					:label="$T('inputs.section-name')"
					:items="sections"
					item-text="name"
					item-value="id"
					dense
					outlined
					hide-details
				/>
			</v-col>

			<!-- jobDescriptionId -->
			<v-col lg="2" md="3" cols="6">
				<v-autocomplete
					v-model="filterData.jobDescriptionId"
					:label="$T('inputs.employee-position')"
					:items="positions"
					item-text="name"
					item-value="id"
					dense
					outlined
					hide-details
				/>
			</v-col>

			<!-- search -->
			<v-col cols="auto">
				<v-btn
					class="me-1"
					outlined
					small
					color="primary"
					:disabled="loading"
					@click="filter()"
				>
					<v-icon small left>mdi-magnify</v-icon>
					{{ $T('default-buttons.search') }}
				</v-btn>
			</v-col>

			<v-spacer/>

			<!-- calculate employees -->
			<v-col cols="auto">
				<v-btn small color="primary" :loading="submitLoading" @click="dialog = true">
					<v-icon size="20" class="me-1">mdi-cash-multiple</v-icon>
					{{ $T('calculate-salaries-for-selected-month') }}
				</v-btn>
			</v-col>

			<!-- export excel -->
			<v-col v-if="0" cols="auto">
				<v-btn
					small
					color="secondary"
					:loading="exportExcelLoading"
					@click="exportExcel"
				>
					<v-icon size="20" class="me-1">mdi-file-table-outline</v-icon>
					{{ $T("exportToExcel") }}
				</v-btn>
			</v-col>

			<v-col v-if="0" cols="auto">
				<v-btn
					small
					color="primary"
					:to="{
						name: 'add-salaries',
						query: { name: new Date().toISOString().slice(0, 10) },
					}"
				>
					<v-icon left> $add </v-icon>
					{{ $T("addSalary") }}
				</v-btn>
			</v-col>
		</v-row>

		<!-- table -->
		<bee-handy-table
			ref="table"
			:headers="headers"
			:items="items"
			:data-table-props="{ disableSort: true }"
			:items-per-page="-1"
			:loading="loading"
			dense
			zebra
			fixed-header
			:height="$vuetify.breakpoint.height - 110"
			:shown-rows-text="$T('shown-rows-text')"
			hide-default-footer
		></bee-handy-table>
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters } from 'vuex';
import rules from '@/utils/validation rules';

export default {
	name: 'SalariesDeatials',

	data() {
		return {
			loading: false,
			submitLoading: false,
			exportExcelLoading: false,
			totalItemsLength: 0,

			detailsLoading: false,
			detailsDialog: false,
			detailsData: {
				recompenses: [],
				deductions: [],
				absenceDiscount: 0,
				delayDiscount: 0,
				financialPenalties: 0,
				financialRewards: 0,
				additional: 0,
				baseSalary: 0,
				finalSalary: 0
			},
			
			employeeId: null,

			rules,

			filterData: {
				searchDate: new Date().toISOString().slice(0, 7),
				sectionId: null,
				fromDate: moment(this.moment().startOf('month')).format('YYYY-MM-DD'),
				toDate: moment(this.moment()).format('YYYY-MM-DD'),
				jobDescriptionId: null
			},

			dialog: false,
			dialogData: {
				sectionId: null,
				employeeIds: [],
				month: null,
			},
			items: []
		};
	},

	watch: {
		detailsDialog(val) {
			if (val) {
				if (this.employeeId) { this.fetchEmployeeDetailedSalary(); }
			} else {
				this.employeeId = null;
				this.detailsData.recompenses = [];
				this.detailsData.deductions = [];
				this.detailsData.absenceDiscount = null;
				this.detailsData.delayDiscount = null;
				this.detailsData.financialPenalties = null;
				this.detailsData.financialRewards = null;
				this.detailsData.additional = null;
				this.detailsData.baseSalary = null;
				this.detailsData.finalSalary = null;
			}
		}
	},

	computed: {
		...mapState({
			sections: state => state.section.sections,
			positions: state => state.positions.positions,
			employees: state => state.employee.employees,
		}),
		...mapGetters({
            getPositionById: 'positions/getPositionById',
        }),

		headers() {
// 			[
//   {
//     "jobDescription": "string",
//     "section": "string",
//     "attendHoursCount": 0,
//     "fulledAttendHoursCount": 0,
//     "unjustifiedOutOfWorkCount": 0,
//     "leavesWithSalaryHoursCount": 0,
//     "leavesWithoutSalaryHoursCount": 0,
//     "additionalAssignmentHoursCount": 0,
//     "additionalUnAssignmentHoursCount": 0,
//     "holidayHoursCount": 0,
//     "delayHoursCount": 0,
//     "earlyDepartureHoursCount": 0
//   }
// ]
			return [
				{
					text: this.$t('headers.employee-name'),
					name: 'employeeName',
					value: 'employeeName'
				},
				{
					text: this.$T('headers.position-name'),
					name: 'jopDescription',
					value: 'jobDescription'
				},
				{
					text: this.$T('headers.section-name'),
					name: 'section',
					value: 'section'
				},
				{
					text: this.$T('headers.actual-number-of-working-hours'),
					name: 'fulledAttendHoursCount',
					value: 'fulledAttendHoursCount',
				},
				{
					text: this.$T('headers.number-of-paid-leave-hours'),
					name: 'leavesWithSalaryHoursCount',
					value: 'leavesWithSalaryHoursCount'
				},
				{
					text: this.$T('headers.number-of-hours-of-unpaid-leave'),
					name: 'leavesWithoutSalaryHoursCount',
					value: 'leavesWithoutSalaryHoursCount'
				},
				{
					text: this.$T('headers.overtime-hours'),
					name: '',
					value: ''
				},
				{
					text: this.$T('headers.leaves'),
					name: '',
					value: ''
				},
				{
					text: '',
					name: 'actions',
					value: 'actions'
				}
			];
		},

		recompensesHeaders() {
			return [
				{ text: this.$T('headers.recompense'), value: 'name', name: 'name' },
				{
					text: this.$T('headers.value'),
					value: 'value',
					name: 'value',
					align: 'center',
					width: '40%',
					columnClass: 'px-2',
					noNotAvailableText: true,
				}
			];
		},
		deductionsHeaders() {
			return [
				{ text: this.$T('headers.deduction'), value: 'name', name: 'name' },
				{
					text: this.$T('headers.value'),
					value: 'value',
					name: 'value',
					align: 'center',
					width: '40%',
					columnClass: 'px-2',
					noNotAvailableText: true,
				}
			];
		},
	},

	methods: {
		moment,

		filter() {
			this.loading = true;
			this.$store.dispatch('salaries/fetchSalariesDetails', {
				from: this.filterData.fromDate,
				to: this.filterData.toDate,
				sectionId: this.filterData.sectionId,
				jobDescription: this.filterData.jobDescriptionId,
			}).then((data) => {
				this.items = data
			}).finally(() => {
				this.loading = false;
			})
		},

		exportExcel() {
			this.exportExcelLoading = true;
			this.$store.dispatch('salaries/exportExcelSalaries', {
				date: this.filterData.searchDate + '-01'
			}).finally(() => {
				this.exportExcelLoading = false;
			})
		},

		calculateEmployeesSalaries() {
			if (this.$refs.form.validate()) {
				this.submitLoading = true;
				this.$store.dispatch('salaries/calculateEmployeesSalaries', {
					sectionId: this.dialogData.sectionId,
					employeeIds: this.dialogData.employeeIds,
					month: this.dialogData.month + '-01',
				}).then(() => {
					this.filter();
					this.dialog = false;
				}).finally(() => {
					this.submitLoading = false;
				});
			}
		},

		fetchEmployeeDetailedSalary() {
			this.detailsLoading = true;
			this.$store.dispatch('salaries/fetchEmployeeSalaryDetails', {
				employeeId: 'e3568aa0-7089-494d-0fb3-08dc74155811' || this.employeeId,
				date: this.filterData.searchDate
			}).then((data) => {
				this.detailsData.recompenses = data.recompenseResponseData;
				this.detailsData.deductions = data.deductionResponseData;
				this.detailsData.absenceDiscount = data.absenceDiscount;
				this.detailsData.delayDiscount = data.delayDiscount;
				this.detailsData.financialPenalties = data.financialPenalties;
				this.detailsData.financialRewards = data.financialRewards;
				this.detailsData.additional = data.additional;
				this.detailsData.baseSalary = data.baseSalary;
				this.detailsData.finalSalary = data.finalSalary;
			}).finally(() => {
				this.detailsLoading = false;
			})
		}
	},

	created() {
		// this.filter();
	}
};
</script>
